/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { css, cx } from 'emotion';
import { border, neutral, text } from '@guardian/src-foundations/palette';
import { textSans } from '@guardian/src-foundations/typography';
import { from } from '@guardian/src-foundations/mq';
import { Display } from '@root/src/lib/display';
/*
    This file's values are meant to mirror the values used by frontend.
    Look for marks:
        432b3a46-90c1-4573-90d3-2400b51af8d0
        1b109a4a-791c-4214-acd2-2720d7d9f96f
    ... in the frontend code
 */
var Size;
(function (Size) {
    // standard ad sizes
    Size["billboard"] = "970,250";
    Size["leaderboard"] = "728,90";
    Size["mpu"] = "300,250";
    Size["halfPage"] = "300,600";
    Size["portrait"] = "300,1050";
    Size["skyscraper"] = "160,600";
    Size["mobilesticky"] = "320,50";
    // dfp proprietary ad sizes
    Size["fluid"] = "fluid";
    Size["outOfPage"] = "1,1";
    Size["googleCard"] = "300,274";
    // guardian proprietary ad sizes
    Size["video"] = "620,1";
    Size["outstreamDesktop"] = "620,350";
    Size["outstreamGoogleDesktop"] = "550,310";
    Size["outstreamMobile"] = "300,197";
    Size["merchandisingHighAdFeature"] = "88,89";
    Size["merchandisingHigh"] = "88,87";
    Size["merchandising"] = "88,88";
    Size["inlineMerchandising"] = "88,85";
    Size["fabric"] = "88,71";
    Size["empty"] = "2,2";
})(Size || (Size = {}));
export const labelStyles = css `
    .ad-slot__label,
    .ad-slot__scroll {
        ${textSans.xsmall()};
        position: relative;
        height: 24px;
        background-color: ${neutral[97]};
        padding: 0 8px;
        border-top: 1px solid ${border.secondary};
        color: ${text.supporting};
        text-align: left;
        box-sizing: border-box;
    }

    .ad-slot__close-button {
        display: none;
    }

    .ad-slot__scroll {
        position: fixed;
        bottom: 0;
        width: 100%;
    }
`;
const mobileStickyAdStyles = css `
    position: fixed;
    bottom: 0;
    width: 320px;
    margin: 0 auto;
    right: 0;
    left: 0;
    z-index: 1010;
    ${from.phablet} {
        display: none;
    }
    .ad-slot__close-button {
        display: none;
        position: absolute;
        right: 3px;
        top: 3px;
        padding: 0;
        border: 0;
        height: 21px;
        width: 21px;
        background-color: transparent;
    }
    .ad-slot__close-button svg {
        height: 0.75rem;
        width: 0.75rem;
        stroke: ${neutral[7]};
        fill: ${neutral[7]};
        stroke-linecap: round;
        stroke-width: 0;
        text-align: center;
    }
    .ad-slot--mobile-sticky .ad-slot__label .ad-slot__close-button {
        display: block;
    }
    .ad-slot__close-button__x {
        stroke: ${neutral[7]};
        fill: transparent;
        stroke-linecap: round;
        stroke-width: 2;
        text-align: center;
    }
    .ad-slot__label {
        font-size: 0.75rem;
        line-height: 1.25rem;
        position: relative;
        height: 1.5rem;
        background-color: ${neutral[97]};
        padding: 0 0.5rem;
        border-top: 0.0625rem solid ${border.secondary};
        color: ${neutral[60]};
        text-align: left;
        box-sizing: border-box;
        ${textSans.xsmall()};
    }
`;
export const AdSlot = ({ position, display }) => {
    switch (position) {
        case 'right':
            switch (display) {
                case Display.Immersive:
                case Display.Showcase: {
                    return (React.createElement("div", { id: "dfp-ad--right", className: cx('js-ad-slot', 'ad-slot', 'ad-slot--right', 'ad-slot--mpu-banner-ad', 'ad-slot--rendered', 'js-sticky-mpu', labelStyles), "data-link-name": "ad slot right", "data-name": "right", "data-mobile": [
                            `${Size.outOfPage}`,
                            `${Size.empty}`,
                            `${Size.mpu}`,
                            `${Size.googleCard}`,
                            `${Size.halfPage}`,
                            `${Size.fluid}`,
                        ].join('|'), "aria-hidden": "true" }));
                }
                case Display.Standard: {
                    const MOSTVIEWED_STICKY_HEIGHT = 1059;
                    return (React.createElement("div", { className: css `
                                position: static;
                                height: ${MOSTVIEWED_STICKY_HEIGHT}px;
                            ` },
                        React.createElement("div", { id: "dfp-ad--right", className: cx('js-ad-slot', 'ad-slot', 'ad-slot--right', 'ad-slot--mpu-banner-ad', 'ad-slot--rendered', 'js-sticky-mpu', css `
                                        position: sticky;
                                        top: 0;
                                    `, labelStyles), "data-link-name": "ad slot right", "data-name": "right", "data-mobile": [
                                `${Size.outOfPage}`,
                                `${Size.empty}`,
                                `${Size.mpu}`,
                                `${Size.googleCard}`,
                                `${Size.halfPage}`,
                                `${Size.fluid}`,
                            ].join('|'), "aria-hidden": "true" })));
                }
            }
            break;
        case 'comments': {
            return (React.createElement("div", { className: css `
                        position: static;
                        height: 100%;
                    ` },
                React.createElement("div", { id: "dfp-ad--comments", className: cx('js-ad-slot', 'ad-slot', 'ad-slot--comments', 'ad-slot--mpu-banner-ad', 'ad-slot--rendered', 'js-sticky-mpu', css `
                                position: sticky;
                                top: 0;
                            `, labelStyles), "data-link-name": "ad slot comments", "data-name": "comments", "data-mobile": [
                        `${Size.outOfPage}`,
                        `${Size.empty}`,
                        `${Size.halfPage}`,
                        `${Size.fluid}`,
                    ].join('|'), "data-desktop": [
                        `${Size.outOfPage}`,
                        `${Size.empty}`,
                        `${Size.video}`,
                        `${Size.outstreamDesktop}`,
                        `${Size.outstreamGoogleDesktop}`,
                        `${Size.fluid}`,
                        `${Size.halfPage}`,
                        `${Size.skyscraper}`,
                    ].join('|'), "data-phablet": [
                        `${Size.outOfPage}`,
                        `${Size.empty}`,
                        `${Size.outstreamDesktop}`,
                        `${Size.outstreamGoogleDesktop}`,
                        `${Size.fluid}`,
                    ].join('|'), "aria-hidden": "true" })));
        }
        case 'top-above-nav': {
            const adSlotAboveNav = css `
                margin: 0 auto;
                height: 151px;
                padding-bottom: 18px;
                padding-top: 18px;
                text-align: left;
                display: table;
                width: 728px;
            `;
            return (React.createElement("div", { id: "dfp-ad--top-above-nav", className: cx('js-ad-slot', 'ad-slot', 'ad-slot--top-above-nav', 'ad-slot--mpu-banner-ad', 'ad-slot--rendered', css `
                            position: relative;
                        `, labelStyles, adSlotAboveNav), "data-link-name": "ad slot top-above-nav", "data-name": "top-above-nav", "data-tablet": [
                    `${Size.outOfPage}`,
                    `${Size.empty}`,
                    `${Size.fabric}`,
                    `${Size.fluid}`,
                    `${Size.leaderboard}`,
                ].join('|'), "data-desktop": [
                    `${Size.outOfPage}`,
                    `${Size.empty}`,
                    `${Size.leaderboard}`,
                    `940,230`,
                    `900,250`,
                    `${Size.billboard}`,
                    `${Size.fabric}`,
                    `${Size.fluid}`,
                ].join('|'), "aria-hidden": "true" }));
        }
        case 'mostpop': {
            return (React.createElement("div", { id: "dfp-ad--mostpop", className: cx('js-ad-slot', 'ad-slot', 'ad-slot--mostpop', 'ad-slot--mpu-banner-ad', 'ad-slot--rendered', css `
                            position: relative;
                        `, labelStyles), "data-link-name": "ad slot mostpop", "data-name": "mostpop", "data-mobile": [
                    `${Size.outOfPage}`,
                    `${Size.empty}`,
                    `${Size.mpu}`,
                    `${Size.googleCard}`,
                    `${Size.fluid}`,
                ].join('|'), "data-tablet": [
                    `${Size.outOfPage}`,
                    `${Size.empty}`,
                    `${Size.mpu}`,
                    `${Size.googleCard}`,
                    `${Size.halfPage}`,
                    `${Size.leaderboard}`,
                    `${Size.fluid}`,
                ].join('|'), "data-phablet": [
                    `${Size.outOfPage}`,
                    `${Size.empty}`,
                    `${Size.outstreamMobile}`,
                    `${Size.mpu}`,
                    `${Size.googleCard}`,
                    `${Size.halfPage}`,
                    `${Size.outstreamGoogleDesktop}`,
                    `${Size.fluid}`,
                ].join('|'), "data-desktop": [
                    `${Size.outOfPage}`,
                    `${Size.empty}`,
                    `${Size.mpu}`,
                    `${Size.googleCard}`,
                    `${Size.halfPage}`,
                    `${Size.fluid}`,
                ].join('|'), "aria-hidden": "true" }));
        }
        case 'merchandising-high': {
            return (React.createElement("div", { id: "dfp-ad--merchandising-high", className: cx('js-ad-slot', 'ad-slot', 'ad-slot--merchandising-high', css `
                            position: relative;
                        `, labelStyles), "data-link-name": "ad slot merchandising-high", "data-name": "merchandising-high", "data-mobile": [
                    `${Size.outOfPage}`,
                    `${Size.empty}`,
                    `${Size.merchandisingHigh}`,
                    `${Size.fluid}`,
                ].join('|'), "aria-hidden": "true" }));
        }
        case 'merchandising': {
            return (React.createElement("div", { id: "dfp-ad--merchandising", className: cx('js-ad-slot', 'ad-slot', 'ad-slot--merchandising', css `
                            position: relative;
                        `, labelStyles), "data-link-name": "ad slot merchandising", "data-name": "merchandising", "data-mobile": [
                    `${Size.outOfPage}`,
                    `${Size.empty}`,
                    `${Size.merchandising}`,
                    `${Size.fluid}`,
                ].join('|'), "aria-hidden": "true" }));
        }
    }
};
export const MobileStickyContainer = ({}) => {
    return React.createElement("div", { className: `mobilesticky-container ${mobileStickyAdStyles}` });
};
